import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, MenuItem, FormControl, InputLabel, Select, IconButton, InputAdornment, Snackbar, Alert, Paper, Box } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '../utils/errorHandler';
import PasswordRequirements from './PasswordRequirements';
import { validatePassword } from '../utils/passwordValidation';

const RegisterUser = () => {
    const [user, setUser] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        password: '',
        role: 'employee',
        location: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [userLocations, setUserLocations] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
        }
    }, [navigate]);

    // Fetch locations from backend
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await api.get('/config/locations');
                setUserLocations(response.data.userLocations || []);
            } catch (error) {
                console.error('Error fetching locations:', error);
                // Fallback to default locations if API fails
                setUserLocations(['Mill', 'Godown', 'Nora', 'Universal']);
            }
        };
        fetchLocations();
    }, []);

    const handleChange = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: '' });
    };

    const validateForm = () => {
        let tempErrors = {};

        if (!user.name.trim()) {
            tempErrors.name = 'Name is required';
        }

        // Email validation
        if (!/\S+@\S+\.\S+/.test(user.email)) {
            tempErrors.email = 'Invalid email address';
        }

        // Phone number validation
        if (!/^\d{10}$/.test(user.phoneNumber)) {
            tempErrors.phoneNumber = 'Phone number must be 10 digits';
        }

        // Password validation
        const { isValid, errors: passwordErrors } = validatePassword(user.password);
        if (!isValid) {
            tempErrors.password = passwordErrors.join(', ');
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if(validateForm()) {
            setIsLoading(true);
            try {
                await api.post('/auth/register-by-owner', user);
                setSnackbar({ open: true, message: 'User registered successfully', severity: 'success' });
                //alert('User registered successfully. An email with credentials has been sent to the user.');
                setUser({ name: '', email: '', phoneNumber: '', password: '', role: 'employee', location: '' });
            } catch (error) {
                handleApiError(error, setSnackbar);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            color: 'primary.main',
                            mb: 0.5,
                            textAlign: 'center'
                        }}
                    >
                        Register New User
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                        Create a new user account for the system
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        margin="normal"
                        name="name"
                        label="Name"
                        variant="outlined"
                        value={user.name}
                        onChange={handleChange}
                        onFocus={(e) => e.target.select()}
                        required
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        name="email"
                        label="Email"
                        type="email"
                        variant="outlined"
                        value={user.email}
                        onChange={handleChange}
                        onFocus={(e) => e.target.select()}
                        required
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        name="phoneNumber"
                        label="Phone Number"
                        variant="outlined"
                        value={user.phoneNumber}
                        onChange={handleChange}
                        onFocus={(e) => e.target.select()}
                        required
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        name="password"
                        label="Temporary Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        value={user.password}
                        onChange={handleChange}
                        onFocus={(e) => e.target.select()}
                        required
                        error={!!errors.password}
                        helperText={errors.password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />

                    {/* Password Requirements Display */}
                    <PasswordRequirements password={user.password} />
                    <FormControl
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    >
                        <InputLabel>Role</InputLabel>
                        <Select
                            name="role"
                            value={user.role}
                            onChange={handleChange}
                            label="Role"
                            required
                        >
                            <MenuItem value="owner">Owner</MenuItem>
                            <MenuItem value="employee">Employee</MenuItem>
                            <MenuItem value="delivery_person">Delivery Person</MenuItem>
                        </Select>
                    </FormControl>

                    {user.role === 'employee' && (
                        <FormControl
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        >
                            <InputLabel>Location</InputLabel>
                            <Select
                                name="location"
                                value={user.location}
                                onChange={handleChange}
                                label="Location"
                                required
                            >
                                {userLocations.map((location) => (
                                    <MenuItem key={location} value={location}>
                                        {location}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{
                            py: 1.5,
                            fontSize: '1rem',
                            mt: 3,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                            '&:hover': {
                                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                            }
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Registering...' : 'Register User'}
                    </Button>
                </form>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default RegisterUser;